import React from 'react';
import { X, Sparkles } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { useIsMobile } from '../../hooks/useMediaQuery';
import SocialPostGenerator from './SocialPostGenerator';

/**
 * SocialPostDialog
 *
 * Mobile (≤ 768px): full-screen white sheet with a green primary header bar.
 * Desktop: shadcn/ui Dialog ~540px wide with the standard close X.
 *
 * Renders nothing when `open` is false. Each caller controls its own open
 * state so closing & reopening yields a fresh generator instance (the body
 * unmounts when open flips to false).
 */
const SocialPostDialog = ({ open, onOpenChange }) => {
  const isMobile = useIsMobile();

  if (!open) return null;

  if (isMobile) {
    return (
      <div
        className="fixed inset-0 z-50 bg-white flex flex-col"
        data-testid="social-post-dialog-mobile"
        role="dialog"
        aria-modal="true"
        aria-label="Social Post"
      >
        {/* Primary header bar — sits below the iOS status bar via safe-area inset */}
        <header
          className="bg-primary text-white px-4 pb-3 pt-3 flex items-center justify-between shadow-sm"
          style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 1.25rem)' }}
        >
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5" />
            <h2 className="text-lg font-semibold">Social Post</h2>
          </div>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            aria-label="Close"
            className="w-9 h-9 -mr-2 flex items-center justify-center rounded-full hover:bg-white/10 active:bg-white/20"
            data-testid="social-post-close-mobile"
          >
            <X className="w-5 h-5" />
          </button>
        </header>

        <div
          className="flex-1 overflow-y-auto px-4 py-4"
          style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 1rem)' }}
        >
          <SocialPostGenerator />
        </div>
      </div>
    );
  }

  // Desktop modal
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[540px] max-h-[90vh] overflow-y-auto"
        data-testid="social-post-dialog-desktop"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" />
            Social Post
          </DialogTitle>
        </DialogHeader>
        <SocialPostGenerator />
      </DialogContent>
    </Dialog>
  );
};

export default SocialPostDialog;
